import { useEffect, useState } from "react";
import { FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../lib/permissions";

const empty = {
  nombre: "",
  nif: "",
  creditor_id: "",
  iban: "",
  bic: "",
  direccion: "",
  codigo_postal: "",
  ciudad: "",
  provincia: "",
};

export default function DatosOrganizacion() {
  const { can } = useAuth();
  const canEdit = can(PERMISSIONS.MANAGE_ORGANIZATION_CONFIG);

  const [data, setData] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await api.get("/falla-config");
        setData({ ...empty, ...res.data });
      } catch (err) {
        toast.error("No se pudo cargar la configuración");
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  const onChange = (field) => (event) => {
    setData((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!canEdit || saving) return;

    setSaving(true);

    try {
      const res = await api.put("/falla-config", data);
      setData({ ...empty, ...res.data });
      toast.success("Datos de la organización guardados");
    } catch (err) {
      toast.error("Error: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="py-8 text-med-muted">Cargando configuración…</p>;
  }

  return (
    <form
      onSubmit={onSubmit}
      className="bg-med-surface border border-med-border rounded-2xl p-6 md:p-8 max-w-3xl"
      data-testid="organization-config-form"
    >
      {!canEdit && (
        <div className="mb-6 rounded-xl border border-med-warning/30 bg-med-warning/10 text-med-warning px-4 py-3 text-sm">
          No tienes permiso para modificar estos datos.
        </div>
      )}

      <h2 className="font-display text-xl font-bold text-med-text mb-1">
        Identidad de la organización
      </h2>
      <p className="text-sm text-med-muted mb-5">
        Datos generales de la falla utilizados en la gestión.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <Field
          label="Nombre de la falla *"
          value={data.nombre}
          onChange={onChange("nombre")}
          disabled={!canEdit}
          required
        />
        <Field
          label="NIF / CIF *"
          value={data.nif}
          onChange={onChange("nif")}
          disabled={!canEdit}
          required
        />
      </div>

      <h2 className="font-display text-xl font-bold text-med-text mb-1">
        Dirección
      </h2>
      <p className="text-sm text-med-muted mb-5">
        Dirección de la organización.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Field
            label="Dirección"
            value={data.direccion}
            onChange={onChange("direccion")}
            disabled={!canEdit}
          />
        </div>
        <Field
          label="Código postal"
          value={data.codigo_postal}
          onChange={onChange("codigo_postal")}
          disabled={!canEdit}
        />
        <Field
          label="Ciudad"
          value={data.ciudad}
          onChange={onChange("ciudad")}
          disabled={!canEdit}
        />
        <div className="md:col-span-2">
          <Field
            label="Provincia"
            value={data.provincia}
            onChange={onChange("provincia")}
            disabled={!canEdit}
          />
        </div>
      </div>

      <div className="flex justify-end mt-8 pt-6 border-t border-med-border">
        <button
          type="submit"
          disabled={!canEdit || saving}
          className="inline-flex items-center gap-2 bg-med-primary hover:bg-med-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl px-5 py-2.5 font-semibold transition-colors"
        >
          <FloppyDisk size={18} weight="bold" />
          {saving ? "Guardando…" : "Guardar configuración"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, value, onChange, disabled, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
        {label}
      </label>
      <input
        type="text"
        value={value || ""}
        onChange={onChange}
        disabled={disabled}
        required={required}
        className="w-full rounded-xl bg-white border border-med-border focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none transition-colors disabled:bg-med-surface2 disabled:cursor-not-allowed"
      />
    </div>
  );
}
